<script lang="ts">
	// Stores
	import { deviceMainFilter, devicesFilters, resetFiltersAllDevicesList } from 'src/stores/ui/filters';
	import { edit, update } from 'src/stores/ui/edit_lists';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { formatFirmwareDisplay } from '../../../helpers/firmware_compare_helpers';
	// Services
	import { format } from 'svelte-i18n';
	// Design system components
	import {
		Input,
		Toolbar,
		Breadcrumbs,
		IconButton,
		Chip,
		ToolbarFilterContainer,
		Tooltip,
	} from '@Shelly/shelly-design-system';
	// Components
	import AddEditButtons from '../../AddEditButtons.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import { capFirstChar, removeOrAddUnderscore } from 'src/helpers/utilities';
	import DevicesFilterModal from './DevicesFilterModal.svelte';
	import { querystring, replace, location, push } from 'svelte-spa-router';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { MAX_AI_MODEL_LIMIT } from 'src/constants';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import DeviceListToggleLayout from './DeviceListToggleLayout.svelte';
	import AllDevicesDropdown from './AllDevicesDropdown/AllDevicesDropdown.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { getSharedHomeManagedTooltip } from 'src/helpers/houses/shared_home_messaging_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';

	export let id: string;
	export let type: string;
	export let itemsLength: number = 0;
	export let emptyList: boolean = false;
	export let areFiltersDisabled: boolean = false;

	const dispatch = createEventDispatcher<{ 'select-all': {}; 'submit-click': {} }>();
	const headerHandle = createListHeaderHandle();

	let isMobile = false;
	let openFilterModal = false;
	let showSharedManagedModal = false;

	$: useSecondaryEllipsisVariant = $showNewLayout;

	$: devicesBreadcrumbs = [
		{
			label: $format('tab_all_devices', { default: 'All devices' }),
		},
	];

	$: currentFilters = Object.keys($devicesFilters).reduce((res, filterType) => {
		const filters = $devicesFilters[filterType];
		if (!Array.isArray(filters)) {
			if (typeof filters === 'string' && filters !== '') {
				return res.concat({
					filter: {
						title: `FW ${formatFirmwareDisplay(filters)}`,
						id: filterType,
						selected: true,
					},
					type: filterType,
				});
			}
			return res;
		}

		const selected = filters.reduce((result, filter) => {
			if (filter.selected) {
				return result.concat({ filter, type: filterType });
			} else if (!filter.hasOwnProperty('selected') && filter.title !== 'filter_all_rooms') {
				return result.concat({ filter, type: filterType });
			} else {
				return result;
			}
		}, []);
		return res.concat(selected);
	}, []);

	$: notification =
		currentFilters.length > 0
			? ({
					title: currentFilters.length,
					type: ComponentType.DANGER,
				} as const)
			: null;

	$: devicesFiltersTranslationsMap = {
		online: $format('filter_online', { default: 'Online' }),
		offline: $format('filter_offline', { default: 'Offline' }),
		shared_devices: $format('shared_devices', { default: 'shared devices' }),
		all_shared_devices: $format('all_shared_devices', { default: 'All shared devices' }),
		shared_devices_by_me: $format('shared_devices_by_me', { default: 'Shared devices by me' }),
		hidden_devices: $format('hidden_devices', { default: 'hidden devices' }),
		all_owned_devices: $format('all_owned_devices', { default: 'All owned devices' }),
		cloud_enabled: $format('cloud_online', { default: 'Cloud online' }),
		cloud_disabled: $format('cloud_offline', { default: 'Cloud offline' }),
		range_extenders: $format('filter_range_extenders', { default: 'Range extenders' }),
		active_range_extenders: $format('filter_active_range_extenders', {
			default: 'Active range extenders',
		}),
		bluetooth_devices: $format('filter_bluetooth_devices', { default: 'Bluetooth devices' }),
		bluetooth_gateways: $format('filter_bluetooth_gateways', { default: 'Bluetooth gateways' }),
		active_bluetooth_gateways: $format('filter_active_bluetooth_gateways', {
			default: 'Active bluetooth gateways',
		}),
		lighting: $format('appliance_type_lighting', { default: 'Lighting' }),
		entertainment: $format('appliance_type_entertainment', { default: 'Entertainment' }),
		heating_and_cooling: $format('appliance_type_heating_cooling', {
			default: 'Heating and cooling',
		}),
		refrigeration: $format('appliance_type_refrigeration', { default: 'Refrigeration' }),
		laundry_and_cleaning: $format('appliance_type_laundry_cleaning', {
			default: 'Laundry and cleaning',
		}),
		cooking: $format('appliance_type_cooking', { default: 'Cooking' }),
		pool_and_garden: $format('appliance_type_pool_garden', { default: 'Pool and garden' }),
		electric_vehicle: $format('appliance_type_electric_vehicle', {
			default: 'Electric vehicle',
		}),
		ventilation: $format('appliance_type_ventilation', { default: 'Ventilation' }),
		grid_source: $format('appliance_type_grid_source', { default: 'Grid source' }),
		generated_energy: $format('appliance_type_generated_energy', {
			default: 'Generated energy',
		}),
		water_heater: $format('appliance_type_water_heater', { default: 'Water heater' }),
		roller: $format('appliance_type_roller', { default: 'Roller' }),
		garage_door: $format('appliance_type_garage_door', { default: 'Garage door' }),
		active_AI_models: `${itemsLength}/${MAX_AI_MODEL_LIMIT} ${$format(
			'filter_active_number_ai_models',
			{
				default: 'Active AI models',
			},
		)}`,
		other: $format('appliance_type_other', { default: 'Other' }),
		virtual_devices: $format('filter_virtual_devices', { default: 'Virtual Devices' }),
		peripheral_devices: $format('filter_peripheral_devices', { default: 'Peripheral Devices' }),
	};

	const resetFilterByIdAndType = (filterId?, filterType?) => {
		if (!Array.isArray($devicesFilters[filterType])) {
			devicesFilters.set({ ...$devicesFilters, [filterType]: '' });
			replace('/home/devices/-1');
			return;
		}
		const updatedFilters = {
			...$devicesFilters,
			[filterType]: $devicesFilters[filterType].map((filter) => {
				if (filter.id === filterId) {
					return { ...filter, selected: false };
				} else if (!filter.hasOwnProperty('id') && filterId === filter.value) {
					return { ...defaultSelectedRoom };
				}
				return filter;
			}),
		};
		devicesFilters.set(updatedFilters);

		replace('/home/devices/-1');
	};

	// route filter for energy tab deep-links (kept from the pre-refactor logic)
	const routeFilter = Number($querystring.split('=')[1]);

	if (routeFilter) {
		Object.values($devicesFilters.appliance_types).map((filter) => {
			if (filter.id === routeFilter) {
				filter.selected = true;
			} else {
				filter.selected = false;
			}
			return filter;
		});
		replace(`${$location}`);
	}

	// New-layout publisher — only for the "All devices" flat list
	// (type === 'devices'). The room-scoped path is handled by
	// `RoomToolbarWrapper`. Search/filter/chips render inline in the Toolbar
	// template below (new-layout branch); the tabs action slot only carries the
	// unified `AllDevicesDropdown` (bulk actions + change view in one kebab).
	function resetAllFiltersAndRoute() {
		resetFiltersAllDevicesList();
		push('/home/devices/-1');
	}

	$: if ($showNewLayout && type === 'devices') {
		const config: ListHeaderConfig = {
			extraActions: [
				{
					key: 'all-devices-dropdown',
					component: AllDevicesDropdown,
					props: {
						disableBatchCommandButton: emptyList,
						areFiltersDisabled,
						onSelectAll: () => dispatch('select-all'),
						onSubmitClick: () => dispatch('submit-click'),
					},
				},
			],
		};
		console.log('[listHeader] publish DevicesToolbar', {
			type,
			emptyList,
			areFiltersDisabled,
		});
		headerHandle.set(config);
	}

	onDestroy(() => {
		if (type === 'devices') {
			console.log('[listHeader] clear DevicesToolbar', { type });
			headerHandle.clear();
		}
		resetFiltersAllDevicesList();
	});
</script>

{#if $showNewLayout && type === 'devices'}
	<Toolbar
		class="mb-m"
		isSticky
		useNewLayout
		noBreadcrumbs
		hideSlotLeftSide={$edit && !$update.value}
		hideSlotRightSide
		hideSlotBottom={currentFilters.length === 0}
	>
		<svelte:fragment slot="leftSide">
			<div class="d-flex align-items-center justify-content-center">
				<Input
					class="width-md-l mr-sm-s text-capitalize-placeholder"
					bind:value={$deviceMainFilter}
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="devices-toolbar-search-input"
					aria-labelledby="devices-toolbar-search-input"
				/>
				<IconButton
					disabled={areFiltersDisabled}
					icon="fa-solid fa-filter"
					on:click={() => {
						openFilterModal = true;
					}}
					aria-label={$format('aria_open_filter', { default: 'Open filter' })}
					{notification}
				/>
			</div>
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			<ToolbarFilterContainer>
				{#each currentFilters as currentFilter}
					<Chip
						id="chip-2avx4v5"
						aria-label={$format('aria_remove_filter', { default: 'Remove filter' }) +
							' ' +
							(devicesFiltersTranslationsMap[
								removeOrAddUnderscore(currentFilter.filter.title, 'addUnderscore')
							] || currentFilter.filter.title)}
						disabled={areFiltersDisabled}
						showChip={currentFilter.filter.selected ?? currentFilter.filter.title}
						title={devicesFiltersTranslationsMap[
							removeOrAddUnderscore(currentFilter.filter.title, 'addUnderscore')
						] || currentFilter.filter.title}
						on:close={() =>
							resetFilterByIdAndType(
								currentFilter.filter.id || currentFilter.filter.value,
								currentFilter.type,
							)}
					/>
				{/each}
			</ToolbarFilterContainer>

			<IconButton
				disabled={areFiltersDisabled}
				on:click={resetAllFiltersAndRoute}
				icon="fas fa-xmark"
				variant="secondary"
				class="width-sm-full width-md-auto"
			>
				{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
			</IconButton>
		</svelte:fragment>
	</Toolbar>
{:else if !$showNewLayout}
	<Toolbar
		class="mb-m"
		hideSlotBottom={type !== 'devices' || (type === 'devices' && currentFilters.length === 0)}
		isSticky
		useNewLayout={$showNewLayout}
	>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else}
				<Breadcrumbs
					aria-label={$format('label_devices', { default: 'Devices' })}
					class="text-capitalize"
					links={devicesBreadcrumbs}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				{#if isMobile && $update.value}
					<AddEditButtons
						hideAddButton
						on:add_click={async () => {
							edit.set(false);
						}}
						disableEditButton={emptyList}
						on:select-all
						on:submit-click
						bind:areFiltersDisabled
						disableBatchCommandButton={emptyList}
						context="room"
						bind:isMobile
					/>
				{/if}
				<RoomToolbar />
			{/if}
			{#if type === 'devices'}
				{#if isMobile && $update.value}
					<AddEditButtons
						hideAddButton
						hideEditButton
						disableEditMode
						on:select-all
						on:submit-click
						bind:areFiltersDisabled
						context="devices"
						bind:isMobile
						{useSecondaryEllipsisVariant}
					/>
				{/if}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-l mr-sm-s text-capitalize-placeholder"
						bind:value={$deviceMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="devices-toolbar-search-input"
						aria-labelledby="devices-toolbar-search-input"
					/>
					<IconButton
						disabled={areFiltersDisabled}
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			{#if !$edit}
				{#if isActiveHomeShared($homes.list)}
					<Tooltip placement="top" title={getSharedHomeManagedTooltip('change_view')}>
						<DeviceListToggleLayout disabled />
					</Tooltip>
				{:else}
					<DeviceListToggleLayout />
				{/if}
			{/if}

			{#if type === 'room'}
				{#if !isSubAccount() || isActiveHomeShared($homes.list)}
					{#if !$update.value || !isMobile}
						<AddEditButtons
							hideAddButton
							on:add_click={async () => {
								edit.set(false);
							}}
							disableEditButton={emptyList}
							interceptEditClick={isActiveHomeShared($homes.list)}
							on:edit_click={() => (showSharedManagedModal = true)}
							on:select-all
							on:submit-click
							bind:areFiltersDisabled
							disableBatchCommandButton={emptyList}
							context="room"
							bind:isMobile
						/>
					{/if}
				{/if}
			{:else}
				{#if !$update.value || !isMobile}
					<AddEditButtons
						hideAddButton
						hideEditButton
						disableEditMode
						on:select-all
						on:submit-click
						bind:areFiltersDisabled
						context="devices"
						bind:isMobile
						{useSecondaryEllipsisVariant}
					/>
				{:else}
					<AddEditButtons hideAddButton hideEditButton disableEditMode />
				{/if}
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			<ToolbarFilterContainer>
				{#each currentFilters as currentFilter}
					<Chip
						id="chip-2avx4v5"
						aria-label={$format('aria_remove_filter', { default: 'Remove filter' }) +
							' ' +
							(devicesFiltersTranslationsMap[
								removeOrAddUnderscore(currentFilter.filter.title, 'addUnderscore')
							] || currentFilter.filter.title)}
						disabled={areFiltersDisabled}
						showChip={currentFilter.filter.selected ?? currentFilter.filter.title}
						title={devicesFiltersTranslationsMap[
							removeOrAddUnderscore(currentFilter.filter.title, 'addUnderscore')
						] || currentFilter.filter.title}
						on:close={() =>
							resetFilterByIdAndType(
								currentFilter.filter.id || currentFilter.filter.value,
								currentFilter.type,
							)}
					/>
				{/each}
			</ToolbarFilterContainer>

			<IconButton
				disabled={areFiltersDisabled}
				on:click={resetAllFiltersAndRoute}
				icon="fas fa-xmark"
				variant="secondary"
				class="width-sm-full width-md-auto"
			>
				{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
			</IconButton>
		</svelte:fragment>
	</Toolbar>
{/if}

{#if openFilterModal}
	<DevicesFilterModal bind:openFilterModal />
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="edit_entity" />
